import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, clearAntiforgery, refreshAntiforgery } from './api';
import type { CurrentUser, Role } from './types';

export const meKey = ['account', 'me'] as const;

/** The signed-in user, or null when there is no session. */
export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => {
      try {
        return await api.get<CurrentUser>('/api/v1/account/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    // Coming back to the tab after a long break shows the login page at once when the session has ended.
    refetchOnWindowFocus: true,
    retry: false,
  });
}

/**
 * Handles a session that ended on the server (expired or signed out on another device): when any API call is
 * answered with 401, the user is marked as signed out, so protected pages go to the login page instead of
 * showing errors. Register for all queries and mutations (QueryCache/MutationCache onError).
 */
export function signOutWhenSessionEnds(client: QueryClient) {
  return (error: unknown) => {
    if (error instanceof ApiError && error.status === 401 && client.getQueryData(meKey)) {
      clearAntiforgery();
      client.setQueryData(meKey, null);
    }
  };
}

/** Call after any sign-in, sign-out or account change that re-issues the session. */
export function useSessionChanged() {
  const client = useQueryClient();
  return async () => {
    clearAntiforgery();
    await refreshAntiforgery().catch(() => undefined);
    await client.invalidateQueries();
  };
}

export function useLogout() {
  const client = useQueryClient();
  return async () => {
    await api.post('/api/v1/auth/logout').catch(() => undefined);
    clearAntiforgery();
    client.clear();
    window.location.assign('/logowanie');
  };
}

/** "Zobacz jako": the administrator views the application as another user (read-only), and back. */
export function useImpersonation() {
  const client = useQueryClient();
  const reload = (path: string) => {
    clearAntiforgery(); // the token is bound to the signed-in user
    client.clear();
    window.location.assign(path);
  };
  return {
    start: async (userId: string) => {
      await api.post('/api/v1/admin/impersonation', { userId });
      reload('/');
    },
    stop: async () => {
      await api.post('/api/v1/impersonation/stop');
      reload('/');
    },
  };
}

export const hasRole = (user: CurrentUser | null | undefined, ...roles: Role[]) =>
  !!user && user.roles.some((r) => roles.includes(r));

export const isStaff = (user: CurrentUser | null | undefined) => hasRole(user, 'Admin', 'Reception', 'Therapist');
export const isManager = (user: CurrentUser | null | undefined) => hasRole(user, 'Admin', 'Reception');

/** Default landing page per role. */
export function homeFor(user: CurrentUser): string {
  if (hasRole(user, 'Admin', 'Reception')) return '/placowka/kalendarz';
  if (hasRole(user, 'Therapist')) return '/kalendarz';
  return '/wizyty';
}
